import React, { useEffect, useState, useRef } from 'react';
import { t } from '../../i18n';
import { Category, loadCategories, loadCategoriesFromExtension, saveCategoriesToExtension } from '../CategoryManager';
import './style.scss';

interface DailyPageEntry {
  time: number;
  visitCount: number;
}

interface DailyDomainEntry {
  time: number;
}

interface PageData {
  totalTime: number;
  visitCount: number;
  lastVisited: number;
  title: string;
  daily?: Record<string, DailyPageEntry>;
}

interface DomainData {
  totalTime: number;
  lastVisited: number;
  pages: Record<string, PageData>;
  daily?: Record<string, DailyDomainEntry>;
}

interface ViewingTimeData {
  [domain: string]: DomainData;
}

type PeriodFilter = 'all' | 'today' | 'week' | 'month';

interface ViewingTimeDisplayProps {}

const ViewingTimeDisplay: React.FC<ViewingTimeDisplayProps> = () => {
  const [viewingData, setViewingData] = useState<ViewingTimeData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [sortBy, setSortByState] = useState<'time' | 'visits' | 'recent'>(() => (localStorage.getItem('bls_sortBy') as 'time' | 'visits' | 'recent') || 'time');
  const [period, setPeriodState] = useState<PeriodFilter>(() => (localStorage.getItem('bls_period') as PeriodFilter) || 'all');
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [autoRefresh, setAutoRefreshState] = useState<boolean>(() => localStorage.getItem('bls_autoRefresh') === 'true');

  const setSortBy = (v: 'time' | 'visits' | 'recent') => { setSortByState(v); localStorage.setItem('bls_sortBy', v); };
  const setPeriod = (v: PeriodFilter) => { setPeriodState(v); localStorage.setItem('bls_period', v); };
  const setAutoRefresh = (v: boolean) => { setAutoRefreshState(v); localStorage.setItem('bls_autoRefresh', String(v)); };
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  const [storageUsage, setStorageUsage] = useState<number | null>(null);
  const [blacklist, setBlacklist] = useState<string[]>([]);

  const updateViewingData = (data: ViewingTimeData) => {
    setViewingData(data);
    try { setStorageUsage(new Blob([JSON.stringify(data)]).size); } catch (e) {}
  };
  const [addingDomain, setAddingDomain] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tagDropdown, setTagDropdown] = useState<string | null>(null);
  const tagDropdownRef = useRef<HTMLDivElement | null>(null);

  // ブラックリストデータを取得
  const loadBlacklist = () => {
    return new Promise<void>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'BLACKLIST_DATA_RESPONSE') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success && event.data.data) {
            const list = event.data.data.blacklist
              ? event.data.data.blacklist.split('\n').filter((d: string) => d.trim() !== '')
              : [];
            setBlacklist(list);
          }
          resolve();
        }
      };
      window.addEventListener('message', handleMessage);
      window.postMessage({ type: 'BLACKLIST_GET_DATA' }, '*');
      setTimeout(() => { window.removeEventListener('message', handleMessage); resolve(); }, 3000);
    });
  };

  // ドメインをブラックリストに追加
  const addToBlacklist = async (domain: string) => {
    if (blacklist.includes(domain)) return;
    setAddingDomain(domain);
    const newList = [...blacklist, domain];
    const blacklistString = newList.join('\n');

    const success = await new Promise<boolean>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'BLACKLIST_UPDATE_RESPONSE') {
          window.removeEventListener('message', handleMessage);
          resolve(event.data.success);
        }
      };
      window.addEventListener('message', handleMessage);
      window.postMessage({ type: 'BLACKLIST_UPDATE_DATA', data: { blacklist: blacklistString } }, '*');
      setTimeout(() => { window.removeEventListener('message', handleMessage); resolve(false); }, 3000);
    });

    if (success) {
      setBlacklist(newList);
    }
    setAddingDomain(null);
  };

  // カテゴリデータ読み込み
  const loadCategoryData = () => {
    const localCats = loadCategories();
    if (localCats.length > 0) {
      setCategories(localCats);
    }
    loadCategoriesFromExtension().then(cats => {
      if (cats.length > 0 || localCats.length === 0) {
        setCategories(cats);
      }
    });
  };

  // ドメインをタグに追加
  const addToTag = (domain: string, cat: Category) => {
    if (cat.domains.includes(domain)) return;
    const updated = categories.map(c =>
      c.name === cat.name ? { ...c, domains: [...c.domains, domain] } : c
    );
    setCategories(updated);
    localStorage.setItem('bls_categories', JSON.stringify(updated));
    saveCategoriesToExtension(updated);
    setTagDropdown(null);
  };

  // タグドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (tagDropdown && tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setTagDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tagDropdown]);

  // 時間をフォーマットする関数
  const formatTime = (milliseconds: number): string => {
    if (milliseconds < 1000) return `0${t('timeSecond')}`;

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}${t('timeDay')} ${hours % 24}${t('timeHour')} ${minutes % 60}${t('timeMinute')}`;
    } else if (hours > 0) {
      return `${hours}${t('timeHour')} ${minutes % 60}${t('timeMinute')}`;
    } else if (minutes > 0) {
      return `${minutes}${t('timeMinute')} ${seconds % 60}${t('timeSecond')}`;
    } else {
      return `${seconds}${t('timeSecond')}`;
    }
  };

  // 期間に該当する日付キーかどうかを判定する関数
  // 日別キー(YYYY-MM-DD)と月別キー(YYYY-MM)の両方に対応
  const getDateKeysForPeriod = (daily: Record<string, any> | undefined): string[] => {
    if (!daily) return [];
    const keys = Object.keys(daily);
    if (period === 'all') return keys;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return keys.filter(key => {
      const parts = key.split('-');
      if (parts.length === 2) {
        // 月別キー (YYYY-MM)
        const monthStart = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
        const monthEnd = new Date(Number(parts[0]), Number(parts[1]), 0); // 月末
        if (period === 'today') {
          return monthStart <= today && today <= monthEnd;
        } else if (period === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 6);
          return monthEnd >= weekAgo && monthStart <= today;
        } else if (period === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 29);
          return monthEnd >= monthAgo && monthStart <= today;
        }
      } else {
        // 日別キー (YYYY-MM-DD)
        const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (period === 'today') {
          return date.getTime() === today.getTime();
        } else if (period === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 6);
          return date >= weekAgo && date <= today;
        } else if (period === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 29);
          return date >= monthAgo && date <= today;
        }
      }
      return true;
    });
  };

  // ドメインの期間別集計時間を取得
  const getDomainTimeForPeriod = (domainData: DomainData): number => {
    if (period === 'all') return domainData.totalTime;
    const keys = getDateKeysForPeriod(domainData.daily);
    return keys.reduce((sum, key) => sum + (domainData.daily?.[key]?.time || 0), 0);
  };

  // ページの期間別集計時間を取得
  const getPageTimeForPeriod = (pageData: PageData): number => {
    if (period === 'all') return pageData.totalTime;
    const keys = getDateKeysForPeriod(pageData.daily);
    return keys.reduce((sum, key) => sum + (pageData.daily?.[key]?.time || 0), 0);
  };

  // ページの期間別訪問回数を取得
  const getPageVisitsForPeriod = (pageData: PageData): number => {
    if (period === 'all') return pageData.visitCount;
    const keys = getDateKeysForPeriod(pageData.daily);
    return keys.reduce((sum, key) => sum + (pageData.daily?.[key]?.visitCount || 0), 0);
  };

  // 日付をフォーマットする関数
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return `${t('timeToday')} ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInDays === 1) {
      return `${t('timeYesterday')} ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInDays < 7) {
      return `${diffInDays}${t('timeDaysAgo')}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Content Script経由でデータを取得
  const loadDataFromContentScript = () => {
    return new Promise<boolean>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'VIEWING_TIME_DATA_RESPONSE') {
          window.removeEventListener('message', handleMessage);
          
          if (event.data.success && event.data.data) {
            updateViewingData(event.data.data);
            resolve(true);
          } else {
            resolve(false);
          }
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Content Scriptにデータ要求
      window.postMessage({ type: 'VIEWING_TIME_GET_DATA' }, '*');
      
      // タイムアウト設定
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        resolve(false);
      }, 3000);
    });
  };

  // Chrome Storage直接アクセス
  const loadDataFromChromeStorage = () => {
    return new Promise<boolean>((resolve) => {
      try {
        if (window.chrome?.storage?.local) {
          window.chrome.storage.local.get(['viewing_time_data'], (result: any) => {
            if (chrome.runtime.lastError) {
              resolve(false);
              return;
            }
            
            try {
              if (result.viewing_time_data) {
                updateViewingData(result.viewing_time_data);
                resolve(true);
              } else {
                updateViewingData({});
                resolve(true);
              }
            } catch (err) {
              resolve(false);
            }
          });
        } else {
          resolve(false);
        }
      } catch (err) {
        resolve(false);
      }
    });
  };

  // データクリア機能
  const clearViewingData = async () => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'VIEWING_TIME_CLEAR_RESPONSE') {
        window.removeEventListener('message', handleMessage);
        if (event.data.success) {
          updateViewingData({});
          setError('');
        } else {
          setError(t('vtErrorClear'));
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    window.postMessage({ type: 'VIEWING_TIME_CLEAR_DATA' }, '*');
    
    setTimeout(() => {
      window.removeEventListener('message', handleMessage);
    }, 3000);
  };

  // データ更新機能
  const refreshViewingData = React.useCallback(async () => {
    setRefreshing(true);
    setError('');
    
    
    // 1. Content Scriptと通信を試す
    const contentSuccess = await loadDataFromContentScript();
    if (contentSuccess) {
      setRefreshing(false);
      setLastRefresh(Date.now());
      return;
    }
    
    // 2. Chrome Storageに直接アクセスを試す
    const storageSuccess = await loadDataFromChromeStorage();
    if (storageSuccess) {
      setRefreshing(false);
      setLastRefresh(Date.now());
      return;
    }

    // 3. すべて失敗した場合
    setError(t('vtErrorRefresh'));
    setRefreshing(false);
  }, [loadDataFromContentScript, loadDataFromChromeStorage]);

  // ドメインの展開/折りたたみ
  const toggleDomain = (domain: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domain)) {
      newExpanded.delete(domain);
    } else {
      newExpanded.add(domain);
    }
    setExpandedDomains(newExpanded);
  };

  // ドメインをソートする関数
  const getSortedDomains = () => {
    const domains = Object.keys(viewingData).filter(domain => {
      if (period === 'all') return true;
      return getDomainTimeForPeriod(viewingData[domain]) > 0;
    });
    return domains.sort((a, b) => {
      const domainA = viewingData[a];
      const domainB = viewingData[b];

      switch (sortBy) {
        case 'time':
          return getDomainTimeForPeriod(domainB) - getDomainTimeForPeriod(domainA);
        case 'visits':
          const visitsA = Object.values(domainA.pages).reduce((sum, page) => sum + getPageVisitsForPeriod(page), 0);
          const visitsB = Object.values(domainB.pages).reduce((sum, page) => sum + getPageVisitsForPeriod(page), 0);
          return visitsB - visitsA;
        case 'recent':
          return domainB.lastVisited - domainA.lastVisited;
        default:
          return getDomainTimeForPeriod(domainB) - getDomainTimeForPeriod(domainA);
      }
    });
  };

  // ページをソートする関数
  const getSortedPages = (pages: Record<string, PageData>) => {
    const pageEntries = Object.entries(pages).filter(([, pageData]) => {
      if (period === 'all') return true;
      return getPageTimeForPeriod(pageData) > 0;
    });
    return pageEntries.sort((a, b) => {
      switch (sortBy) {
        case 'time':
          return getPageTimeForPeriod(b[1]) - getPageTimeForPeriod(a[1]);
        case 'visits':
          return getPageVisitsForPeriod(b[1]) - getPageVisitsForPeriod(a[1]);
        case 'recent':
          return b[1].lastVisited - a[1].lastVisited;
        default:
          return getPageTimeForPeriod(b[1]) - getPageTimeForPeriod(a[1]);
      }
    });
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // 1. Content Scriptと通信を試す
      const contentSuccess = await loadDataFromContentScript();
      if (contentSuccess) {
        loadBlacklist();
        loadCategoryData();
        setLoading(false);
        setLastRefresh(Date.now());
        return;
      }

      // 2. Chrome Storageに直接アクセスを試す
      const storageSuccess = await loadDataFromChromeStorage();
      if (storageSuccess) {
        loadBlacklist();
        loadCategoryData();
        setLoading(false);
        setLastRefresh(Date.now());
        return;
      }

      // 3. すべて失敗した場合
      setError(t('vtErrorConnection'));
      setLoading(false);
    };

    // 拡張機能準備完了の通知を待つ
    const handleExtensionReady = (event: MessageEvent) => {
      if (event.data.type === 'BLACKLIST_EXTENSION_READY') {
        loadData();
      }
    };

    window.addEventListener('message', handleExtensionReady);
    
    // すぐに試行も行う
    setTimeout(loadData, 500);

    return () => {
      window.removeEventListener('message', handleExtensionReady);
    };
  }, []);

  // 自動更新機能
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(async () => {
      if (!refreshing) {
        await refreshViewingData();
      }
    }, 30000); // 30秒ごと

    return () => clearInterval(interval);
  }, [autoRefresh, refreshing, refreshViewingData]);

  if (loading) {
    return (
      <div className="viewing-time-container">
        <div className="loading-state">
          {t('vtLoading')}
        </div>
      </div>
    );
  }

  const filteredDomains = getSortedDomains();
  const totalDomains = filteredDomains.length;
  const totalTime = filteredDomains.reduce((sum, domain) => sum + getDomainTimeForPeriod(viewingData[domain]), 0);
  const totalVisits = filteredDomains.reduce((sum, domain) =>
    sum + Object.values(viewingData[domain].pages).reduce((pageSum, page) => pageSum + getPageVisitsForPeriod(page), 0), 0
  );

  return (
    <div className="viewing-time-container">
      <div className="viewing-time-header">
        <h2 className="viewing-time-title">
          {t('vtTitle')}
        </h2>
        
        <div className="viewing-time-actions">
          <button
            onClick={refreshViewingData}
            disabled={refreshing}
            className={`action-button ${refreshing ? 'refreshing' : 'refresh'}`}
          >
            {refreshing ? (
              <>
                <span className="spin-icon">🔄</span>
                {t('vtRefreshing')}
              </>
            ) : (
              <>
                {t('vtRefresh')}
              </>
            )}
          </button>
          
          <button
            onClick={clearViewingData}
            disabled={refreshing}
            className={`action-button ${refreshing ? 'disabled' : 'clear'}`}
          >
            {t('vtClear')}
          </button>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {/* 統計サマリー */}
      <div className="stats-grid">
        <div className="stat-card domains">
          <div className="stat-value">{totalDomains}</div>
          <div className="stat-label">{t('vtDomainCount')}</div>
        </div>
        
        <div className="stat-card time">
          <div className="stat-value">{formatTime(totalTime)}</div>
          <div className="stat-label">{t('vtTotalTime')}</div>
        </div>
        
        <div className="stat-card visits">
          <div className="stat-value">{totalVisits}</div>
          <div className="stat-label">{t('vtTotalVisits')}</div>
        </div>
      </div>

      {/* ストレージ使用量 */}
      {storageUsage !== null && (
        <div style={{
          fontSize: '12px',
          color: storageUsage > 8 * 1024 * 1024 ? '#d32f2f' : '#555',
          textAlign: 'right',
          marginBottom: '8px'
        }}>
          {t('vtStorageUsage')}: {(storageUsage / 1024 / 1024).toFixed(2)} MB / 10 MB
          {storageUsage > 8 * 1024 * 1024 && (
            <span style={{ display: 'block', color: '#d32f2f', fontWeight: 'bold', marginTop: '4px' }}>
              {t('vtStorageWarning')}
            </span>
          )}
        </div>
      )}

      {/* ソート選択と自動更新設定 */}
      <div className="controls-bar">
        <div className="sort-controls">
          <label className="sort-label">{t('vtPeriodLabel')}</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as PeriodFilter)}
            className="sort-select"
          >
            <option value="all">{t('vtPeriodAll')}</option>
            <option value="today">{t('vtPeriodToday')}</option>
            <option value="week">{t('vtPeriodWeek')}</option>
            <option value="month">{t('vtPeriodMonth')}</option>
          </select>

          <label className="sort-label" style={{ marginLeft: '16px' }}>{t('vtSortLabel')}</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'time' | 'visits' | 'recent')}
            className="sort-select"
          >
            <option value="time">{t('vtSortTime')}</option>
            <option value="visits">{t('vtSortVisits')}</option>
            <option value="recent">{t('vtSortRecent')}</option>
          </select>
        </div>
        
        <div className="refresh-controls">
          <label className="auto-refresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="auto-refresh-checkbox"
            />
            {t('vtAutoRefresh')}
          </label>
          
          <div className="last-refresh">
            {t('vtLastRefresh')} {formatDate(lastRefresh)}
          </div>
        </div>
      </div>
      
      {period !== 'all' && (
        <p style={{ fontSize: '12px', color: '#555', margin: '0 0 16px', textAlign: 'center' }}>
          {t('vtPeriodNote')}
        </p>
      )}

      {totalDomains === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p className="empty-title">{t('vtEmptyTitle')}</p>
          <p className="empty-description">
            {t('vtEmptyDesc')}
          </p>
        </div>
      ) : (
        <div className="domains-list">
          {filteredDomains.map((domain) => {
            const domainData = viewingData[domain];
            const filteredPages = getSortedPages(domainData.pages);
            const pageCount = filteredPages.length;
            const totalVisits = Object.values(domainData.pages).reduce((sum, page) => sum + getPageVisitsForPeriod(page), 0);
            const isExpanded = expandedDomains.has(domain);
            
            return (
              <div 
                key={domain}
                className="domain-card"
              >
                {/* ドメインヘッダー */}
                <div 
                  onClick={() => toggleDomain(domain)}
                  className={`domain-header ${isExpanded ? 'expanded' : ''}`}
                >
                  <div className="domain-info">
                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                      ▶
                    </span>
                    <div className="domain-details">
                      <div className="domain-name">
                        {domain}
                      </div>
                      <div className="domain-meta">
                        {pageCount} {t('vtPages')} • {totalVisits} {t('vtVisitCount')} • {t('vtLastVisit')} {formatDate(domainData.lastVisited)}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="domain-time">
                      {formatTime(getDomainTimeForPeriod(domainData))}
                    </div>
                    {/* タグに追加 */}
                    <div style={{ position: 'relative' }} ref={tagDropdown === domain ? tagDropdownRef : undefined}>
                      {(() => {
                        const inAllTags = categories.length > 0 && categories.every(c => c.domains.includes(domain));
                        return (
                          <button
                            onClick={(e) => { e.stopPropagation(); setTagDropdown(tagDropdown === domain ? null : domain); }}
                            disabled={inAllTags}
                            style={{
                              padding: '4px 10px',
                              fontSize: '11px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: inAllTags ? 'default' : 'pointer',
                              backgroundColor: inAllTags ? '#e0e0e0' : '#1976d2',
                              color: inAllTags ? '#999' : '#fff',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {inAllTags ? t('vtAddedToTag') : t('vtAddToTag')}
                          </button>
                        );
                      })()}
                      {tagDropdown === domain && (
                        <div style={{
                          position: 'absolute', right: 0, top: '100%', marginTop: '4px',
                          background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100,
                          minWidth: '160px', padding: '4px 0'
                        }}>
                          {categories.length === 0 ? (
                            <div style={{ padding: '8px 12px' }}>
                              <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t('vtNoTags')}</div>
                              <a href="/categories" style={{ fontSize: '12px', color: '#1976d2' }}
                                onClick={(e) => { e.preventDefault(); window.history.pushState({}, '', '/categories'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                              >{t('vtGoTagManage')}</a>
                            </div>
                          ) : (
                            categories.filter(c => !c.domains.includes(domain)).map(cat => (
                              <button
                                key={cat.name}
                                onClick={(e) => { e.stopPropagation(); addToTag(domain, cat); }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  width: '100%', padding: '6px 12px', border: 'none',
                                  background: 'transparent', cursor: 'pointer', fontSize: '12px',
                                  textAlign: 'left'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                              >
                                <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: cat.color, flexShrink: 0 }} />
                                {cat.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); addToBlacklist(domain); }}
                      disabled={blacklist.includes(domain) || addingDomain === domain}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: blacklist.includes(domain) ? 'default' : 'pointer',
                        backgroundColor: blacklist.includes(domain) ? '#e0e0e0' : '#ff5252',
                        color: blacklist.includes(domain) ? '#999' : '#fff',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {blacklist.includes(domain) ? t('vtAddedToBlacklist') : addingDomain === domain ? t('vtAddingToBlacklist') : t('vtAddToBlacklist')}
                    </button>
                  </div>
                </div>
                
                {/* ページ詳細 */}
                {isExpanded && (
                  <div className="pages-list">
                    {filteredPages.map(([url, pageData]) => (
                      <div 
                        key={url}
                        className="page-item"
                      >
                        <div className="page-info">
                          <div className="page-title">
                            {pageData.title}
                          </div>
                          <div className="page-url">
                            {url}
                          </div>
                          <div className="page-meta">
                            {getPageVisitsForPeriod(pageData)} {t('vtVisitCount')} • {t('vtLastVisit')} {formatDate(pageData.lastVisited)}
                          </div>
                        </div>

                        <div className="page-time">
                          {formatTime(getPageTimeForPeriod(pageData))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ViewingTimeDisplay;
